import React, {useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import useConfirm from "../../../hooks/useConfirm";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  Typography
} from "@mui/material";
import {doc, writeBatch} from "firebase/firestore";
import {db} from "../../../config/firebase";
import {PROJECT_VERSION_COLLECTION, PROJECTS_COLLECTION} from "../../../db/collections";
import {toast} from "react-toastify";
import {
  IconCamera,
} from "@tabler/icons-react";

import {Context as AppContext} from "../../../context/AppContext";
import {ProjectUpdateForm} from "./ProjectUpdateForm";
import axios from "../../../config/axios";
import {OrganizationRepository} from "../../../repositories/OrganizationRepository";
import {SessionUMRepository} from "../../../repositories/SessionUMRepository";
import {SignInContext, TOKEN} from "../../../context/SignInContext";
import {v4 as uuid} from "uuid";
import {uploadImage} from "../../../utils/uploadImage";
import {FlowProjectRepository} from "../../../repositories/FlowProjectRepository";


export const ProjectSettings = ({project}) => {
  const {updateProject, publishVersion} = useContext(AppContext);
  const {currentUser} = useContext(SignInContext);
  const [newProject, setNewProject] = useState({});
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [asriProjects, setAsriProjects] = useState([]);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const confirmDelete = useConfirm(
    'Are you sure you want to delete this project?',
  );

  useEffect(() => {
    if (project) {
      setNewProject({
        id: project?.id,
        name: project?.name,
        description: project?.description,
        organization: project?.settings?.organization,
        language: project?.settings?.language,
        lang: project?.settings?.lang || null,
        organization_type: project?.settings?.organization_type,
        speechToText: project?.settings?.speechToText,
        host: project?.settings?.host,
        port: project?.settings?.port,
        protocol: project?.settings?.protocol,
        mode: project?.settings?.mode || "stage",
        deploymentProject: project?.settings?.deploymentProject || null,
        team: project?.team || [],
        is_live: !!project?.configuration?.is_live,
        status: !!project?.settings?.status ? project?.settings?.status : "BUILDING_SCENARIOS",
        projectType: !!project?.settings?.projectType ? project?.settings?.projectType : "VOICE_BOT",
      });
      setImage(project?.settings?.img || null);
    }
  }, [project]);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser]);

  const fetchData = async () => {
    try {
      const response = await axios.get('/api/language-support/all');
      setLanguages(response.data);
      const organizationResponse = await OrganizationRepository.getUserOrganizations(currentUser?.username);
      setOrganizations(organizationResponse?.data || []);
      SessionUMRepository.getUserSessions()
        .then(response => {
          setAsriProjects(response?.data?.filter(item => item?.module?.moduleType === "SPEECH_TO_TEXT") || []);
        })
        .catch(err => console.log(err));
      const userResponse = await axios.get('/api/user/all', null, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem(TOKEN)}`
        }
      });
      const users = userResponse.data?.map(u => u.username);
      setUsers(users);
    } catch (err) {
      toast.error(err.message);
    }
  }


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const org = organizations?.find(org => org?.id === newProject?.organization);
      const imgURL = await createImage();

      let formData = {...project}
      formData.name = newProject?.name;
      formData.description = newProject?.description;
      formData.team = newProject?.team || [];
      formData.settings.organization = newProject?.organization;
      formData.settings.language = newProject?.language;
      formData.settings.lang = newProject?.lang;
      formData.settings.organization_type = org?.organization_type || "";
      formData.settings.speechToText = newProject?.speechToText || null;
      formData.settings.host = newProject?.host || null;
      formData.settings.port = newProject?.port || null;
      formData.settings.protocol = newProject?.protocol || null;
      formData.settings.mode = newProject?.mode || null;
      formData.settings.deploymentProject = newProject?.deploymentProject || null;
      formData.settings.status = newProject?.status || "BUILDING_SCENARIOS";
      formData.settings.projectType = newProject?.projectType || "VOICE_BOT";
      formData.settings.img = imgURL;
      if (!formData?.configuration){
        formData.configuration = {};
      }
      formData.configuration.is_live = newProject?.is_live === undefined || newProject?.is_live === null ? true : newProject?.is_live;
      console.log(formData);

      await publishVersion(formData);
      const fetchVersion = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiProject/${project.id}`
      const projectVersionResponse = await axios.get(fetchVersion)
      await FlowProjectRepository.updateFlow({id: project?.id, project: projectVersionResponse?.data || null})
      toast.success("Project settings updated");
    } catch (e) {
      toast.error(e?.message || "Failed to create project")
    } finally {
      setLoading(false);
    }
  }

  const createImage = async () => {
    if (!image || image === project?.settings?.img) {
      return  image;
    }
    const id = uuid();
    const url =`projects/${id}-${image?.name}`;

    return await uploadImage({img: image, url: url});
  }

  const onDelete = async () => {
    const response = await confirmDelete();
    if (response && newProject?.id) {
      try {
        const batch = writeBatch(db);
        // Delete versions
        if (newProject?.versions?.length) {
          for (const version of newProject?.versions) {
            // Delete version
            const versionRef = doc(db, PROJECT_VERSION_COLLECTION, version?.id);
            batch.delete(versionRef);
          }
        }

        // Delete project
        const projectRef = doc(db, PROJECTS_COLLECTION, newProject?.id || newProject?.uid);
        batch.delete(projectRef);

        await batch.commit();
        // await getUserProjects();

        navigate("/projects")
        // onClose();
      } catch (err) {
        toast.error(err);
      }

      return;
    }
  }


  return (
    <>
      <Grid container maxWidth={"xl"} sx={{mx: 'auto', px: {xs: 2, md: 0}, py: {xs: 2, md: 3}, width: "100%"}}>
        <Grid item xs={12} sx={{p: {xs: 0, md: 2}, pb: {xs: 2, md: 2}}}>
          <Typography variant={"h4"} fontSize={{xs: "1.5rem", md: "2.125rem"}} fontWeight={600}>
            Update Project
          </Typography>
        </Grid>
        <Grid item xs={12} md={5} lg={4} sx={{p: {xs: 0, md: 2}, pb: {xs: 2, md: 2}}}>
          <Stack
            direction={{xs: "row", md: "column"}}
            sx={{borderRadius: {xs: 2, md: 4}, p: {xs: 2, md: 0}}}
            alignItems={"center"}
            gap={{xs: 2, md: 0}}
            className={"border-shadow"}
          >
            <Box sx={{p: {xs: 0, md: 2}, flexShrink: 0}}>
              <Avatar
                src={image !== null ? typeof image === "string" ? image : URL.createObjectURL(image) : null}
                className={"border-shadow"}
                sx={{
                  bgcolor: "transparent",
                  height: {xs: 72, sm: 88, md: 120},
                  width: {xs: 72, sm: 88, md: 120},
                  fontSize: {xs: 32, md: 50},
                  borderRadius: 3,
                  mb: "auto", color: "#153B50"
                }}
              >
                {image ? null : <IconCamera size={36} style={{width: "clamp(36px, 8vw, 50px)", height: "clamp(36px, 8vw, 50px)"}}/>}
              </Avatar>
            </Box>
            <Divider sx={{width: "100%", display: {xs: "none", md: "block"}}}/>
            <Button
              component="label"
              fullWidth
              color={"secondary"}
              sx={{minHeight: {xs: 44, md: "auto"}, width: {xs: "auto", md: "100%"}, px: {xs: 2, md: 1.5}}}
            >
              <Box component="span" sx={{display: {xs: "inline", md: "none"}}}>Change image</Box>
              <Box component="span" sx={{display: {xs: "none", md: "inline"}}}>Upload File</Box>
              <input
                type="file"
                hidden
                onChange={(event) => setImage(event.target.files[0])}
                accept="image/*"
              />
            </Button>
          </Stack>
        </Grid>
        <Grid component={"form"} onSubmit={handleSubmit} item xs={12} md={7} lg={8} sx={{p: {xs: 0, md: 2}}}>
          <Stack direction={"column"} gap={1} sx={{borderRadius: {xs: 2, md: 4}, overflow: {xs: "hidden", md: "visible"}}} className={"border-shadow"}>
            <ProjectUpdateForm
              project={newProject} setProject={setNewProject}
              languages={languages} organizations={organizations}
              fetchData={fetchData} loading={loading}
              asriProjects={asriProjects} users={users}
              onDelete={onDelete}
            />
          </Stack>
        </Grid>

      </Grid>

    </>
  )
}
